import React, { useEffect, useMemo, useState } from 'react';
import { Search, Users, Phone, Mail, Building2, Grid, List } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import MobileFooter from './MobileFooter';
import baseurl from '../Baseurl/baseurl';
import { Link } from 'react-router-dom';
import { Card, Input, Button, Heading, Eyebrow, Loader, EmptyState, Container } from '../ui';

const MembersPage = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [currentUserId, setCurrentUserId] = useState(null);

  const [imageErrors, setImageErrors] = useState(new Set());

  // Get current user ID from localStorage
  useEffect(() => {
    const memberData = localStorage.getItem('memberData');
    if (memberData) {
      try {
        const parsedData = JSON.parse(memberData);
        setCurrentUserId(parsedData.mid);
      } catch (error) {
        console.error('Error parsing member data:', error);
      }
    }
  }, []);

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${baseurl}/api/member/all`);
        const data = await res.json();
        if (data.success) {
          const membersData = Array.isArray(data.data) ? data.data : [];
          setMembers(membersData);
          // Debug: Log baseurl and sample member data
          console.log('Base URL:', baseurl);
          if (membersData.length > 0) {
            console.log('Sample member data:', membersData[0]);
            console.log('Sample profile image path:', membersData[0]?.profile_image);
          }
        } else {
          setError('Failed to load members');
        }
      } catch (e) {
        setError('Failed to load members');
      } finally {
        setLoading(false);
      }
    };
    fetchMembers();
  }, []);

  // Function to construct proper image URL
  const getProfileImageUrl = (profileImage) => {
    if (!profileImage) return null;

    // If it's already a full URL
    if (profileImage.startsWith('http')) {
      return profileImage;
    }

    // If it starts with a slash, append to baseurl
    if (profileImage.startsWith('/')) {
      return `${baseurl}${profileImage}`;
    }

    // Otherwise, assume it's a relative path
    return `${baseurl}/${profileImage}`;
  };

  const filtered = useMemo(() => {
    let filteredMembers = members;

    // Filter out current user's profile (similar to HomePage business filter)
    if (currentUserId) {
      filteredMembers = filteredMembers.filter((m) => m.mid !== currentUserId);
    }

    // Search filter
    if (query) {
      const q = query.toLowerCase();
      filteredMembers = filteredMembers.filter((m) => {
        const first = (m.first_name || '').toLowerCase();
        const last = (m.last_name || '').toLowerCase();
        const full = `${first} ${last}`.trim();
        const kootam = (m.kootam || '').toLowerCase();
        const kovil = (m.kovil || '').toLowerCase();
        const phone = `${m.contact_no || ''} ${m.mobile_no || ''}`.toLowerCase();
        const businessNames = Array.isArray(m.BusinessProfiles)
          ? m.BusinessProfiles.map((b) => (b.company_name || '').toLowerCase()).join(' ')
          : '';
        return (
          first.includes(q) ||
          last.includes(q) ||
          full.includes(q) ||
          kootam.includes(q) ||
          kovil.includes(q) ||
          phone.includes(q) ||
          businessNames.includes(q)
        );
      });
    }

    // Sort by name
    filteredMembers.sort((a, b) => {
      return `${a.first_name || ''} ${a.last_name || ''}`.localeCompare(`${b.first_name || ''} ${b.last_name || ''}`);
    });

    return filteredMembers;
  }, [members, query, currentUserId]);

  return (
    <div className="min-h-screen bg-bg pb-20 lg:pb-0">
      <Header />

      {/* Hero Section */}
      <div className="on-dark bg-navy relative overflow-hidden py-10 sm:py-12 lg:py-16">
        <div className="pointer-events-none absolute inset-0 bg-primary/5" />
        <Container width="wide" className="relative">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-primary/15 ring-1 ring-inset ring-primary/30 sm:h-16 sm:w-16">
                <Users className="w-7 h-7 sm:w-8 sm:h-8 text-primary" />
              </div>
            </div>
            <Eyebrow className="justify-center">Community Directory</Eyebrow>
            <Heading variant="display" className="mt-3">
              Our Community <em className="kbc-serif">Members</em>
            </Heading>
            <p className="mt-3 text-base sm:text-lg text-text max-w-2xl mx-auto">
              Connect with Fellow Community Members, Discover Businesses, and Build Meaningful Relationships
            </p>
          </div>

          {/* Search Section */}
          <div className="max-w-4xl mx-auto">
            <Input
              type="text"
              placeholder="Search Members by Name, Business, Kootam, Kovil, Phone..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              leftIcon={<Search className="h-5 w-5" />}
            />
          </div>
        </Container>
      </div>

      <Container width="wide" className="py-8 sm:py-10">
        {/* Controls */}
        <div className="flex flex-col gap-4 mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
              <h2 className="font-display text-xl sm:text-2xl lg:text-3xl font-bold text-ink">
                {filtered.length} {filtered.length === 1 ? 'Member' : 'Members'} Found
              </h2>
              {query && (
                <Button variant="subtle" size="sm" onClick={() => setQuery('')} className="self-start sm:self-auto">
                  Clear Search
                </Button>
              )}
            </div>

            <div className="flex items-center gap-2 self-start sm:self-auto">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-primary/15 text-primary ring-1 ring-inset ring-primary/30'
                    : 'text-muted hover:text-ink'
                }`}
              >
                <Grid className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'list'
                    ? 'bg-primary/15 text-primary ring-1 ring-inset ring-primary/30'
                    : 'text-muted hover:text-ink'
                }`}
              >
                <List className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
          </div>
        </div>

        {loading && <Loader label="Loading members…" />}

        {error && (
          <Card padding="lg" className="text-center mb-8 border-danger/20 bg-danger/5">
            <div className="text-danger text-4xl mb-4">⚠️</div>
            <p className="text-danger font-medium text-lg">{error}</p>
          </Card>
        )}

        {!loading && !error && (
          <>
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {filtered.map((m) => {
                  const profileImg = getProfileImageUrl(m.profile_image);
                  const businessCount = Array.isArray(m.BusinessProfiles) ? m.BusinessProfiles.length : 0;
                  const hasImageError = imageErrors.has(m.mid);

                  return (
                    <Card key={m.mid} interactive padding="none" className="overflow-hidden">
                      {/* Card Header with Aurora band */}
                      <div className="on-dark bg-navy relative h-24 sm:h-28">
                        <div className="absolute inset-0 bg-line/10" />
                        <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4">
                          <div className="flex items-center gap-2 sm:gap-3">
                            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg bg-surface p-0.5 sm:p-1 shadow-card flex-shrink-0">
                              <div className="w-full h-full rounded-md overflow-hidden bg-primary/10 flex items-center justify-center">
                                {profileImg && !hasImageError ? (
                                  <img
                                    src={profileImg}
                                    alt={m.first_name}
                                    className="w-full h-full object-cover"
                                    onError={() => {
                                      setImageErrors(prev => new Set([...prev, m.mid]));
                                    }}
                                    onLoad={() => {
                                      setImageErrors(prev => {
                                        const newSet = new Set(prev);
                                        newSet.delete(m.mid);
                                        return newSet;
                                      });
                                    }}
                                  />
                                ) : null}
                                <span
                                  className={`text-primary font-bold text-lg sm:text-xl ${profileImg && !hasImageError ? 'hidden' : 'flex'} items-center justify-center`}
                                >
                                  {(m.first_name || 'U').charAt(0)}
                                </span>
                              </div>
                            </div>
                            <div className="flex-1 min-w-0 text-left">
                              <h3 className="font-display text-base sm:text-lg font-bold text-ink truncate">{m.first_name} {m.last_name}</h3>
                              <p className="text-text text-xs sm:text-sm truncate">{m.email || m.contact_no}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Card Content */}
                      <div className="p-4 sm:p-6">
                        <div className="space-y-3 sm:space-y-4">

                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2">
                              <div className="p-1.5 sm:p-2 bg-info/10 rounded-md">
                                <Building2 className="w-3 h-3 sm:w-4 sm:h-4 text-info" />
                              </div>
                              <div className="text-left">
                                <p className="text-sm sm:text-base font-medium text-ink">{businessCount}</p>
                                <p className="text-xs text-muted">Business</p>
                              </div>
                            </div>

                            <Button as={Link} to={`/member/${m.mid}`} size="sm">
                              View Profile
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <div className="space-y-3 sm:space-y-4">
                {filtered.map((m) => {
                  const profileImg = getProfileImageUrl(m.profile_image);
                  const businessCount = Array.isArray(m.BusinessProfiles) ? m.BusinessProfiles.length : 0;
                  const hasImageError = imageErrors.has(m.mid);

                  return (
                    <Card key={m.mid} interactive padding="md">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
                        {/* Profile Image */}
                        <div className="flex-shrink-0">
                          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg bg-primary/10 p-1 shadow-card">
                            <div className="w-full h-full rounded-md overflow-hidden flex items-center justify-center">
                              {profileImg && !hasImageError ? (
                                <img
                                  src={profileImg}
                                  alt={m.first_name}
                                  className="w-full h-full object-cover"
                                  onError={() => {
                                    setImageErrors(prev => new Set([...prev, m.mid]));
                                  }}
                                  onLoad={() => {
                                    setImageErrors(prev => {
                                      const newSet = new Set(prev);
                                      newSet.delete(m.mid);
                                      return newSet;
                                    });
                                  }}
                                />
                              ) : null}
                              <span
                                className={`text-primary font-bold text-lg sm:text-2xl ${profileImg && !hasImageError ? 'hidden' : 'flex'} items-center justify-center`}
                              >
                                {(m.first_name || 'U').charAt(0)}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Member Info - Left Aligned */}
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col gap-3 sm:gap-4">
                            {/* Name and Contact Info */}
                            <div className="text-left">
                              <h3 className="font-display text-lg sm:text-xl font-bold text-ink mb-2">{m.first_name} {m.last_name}</h3>

                              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 text-xs sm:text-sm text-text mb-3">
                                {m.email && (
                                  <div className="flex items-center gap-2 min-w-0">
                                    <Mail className="w-3 h-3 sm:w-4 sm:h-4 text-primary flex-shrink-0" />
                                    <span className="truncate">{m.email}</span>
                                  </div>
                                )}
                                {m.contact_no && (
                                  <div className="flex items-center gap-2 min-w-0">
                                    <Phone className="w-3 h-3 sm:w-4 sm:h-4 text-primary flex-shrink-0" />
                                    <span className="truncate">{m.contact_no}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Business Count and Action Button - Right Aligned */}
                        <div className="flex items-center gap-4 sm:gap-6 justify-between sm:justify-end">
                          <div className="text-center sm:text-right">
                            <div className="font-display glow-num text-xl sm:text-2xl font-bold text-primary">{businessCount}</div>
                            <div className="text-xs text-muted whitespace-nowrap">Businesses</div>
                          </div>
                          <Button as={Link} to={`/member/${m.mid}`} size="md" className="flex-shrink-0">
                            View Profile
                          </Button>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}

            {filtered.length === 0 && (
              <EmptyState
                icon={<Users className="w-7 h-7" />}
                title="No members found"
                description="Try adjusting your search criteria or filters"
                action={
                  <Button onClick={() => setQuery('')}>
                    Clear Search
                  </Button>
                }
              />
            )}
          </>
        )}
      </Container>

      <Footer />
      <MobileFooter />
    </div>
  );
};

export default MembersPage;
